
import type { CharacterSlot, Characters, ClassOffer } from "../net/messages";
import { Gait } from "../sim/movement";
import { HEADING_STEPS } from "../sim/fixed";
import { CreatureMeshRenderer, hairEntityId, type MeshStatus } from "../render/creaturemesh";
import { drawCreature, type CreatureFrame, type CreaturePalette } from "../render/creature";
import { lookAt, multiply, perspective, type Mat4 } from "../render/view";
import { INTRO_ART, installIntroStyle, introGround, parseEventSet } from "./intro";
import { elem, installChrome, styleInstaller } from "./chrome";

export interface CharacterCreateActions {
  /** Return to character select without sending a creation request. */
  onCancel?: () => void;
  /**
   * Send one creation request. The slot and race came from the server's own
   * frame; name and shape are the player's choices.
   */
  onCreate?: (
    slot: number,
    name: string,
    race: number,
    shape: number,
  ) => void;
}

const JOB_PLATE: readonly string[] = [
  "name_warrior.png",
  "name_assassin.png",
  "name_sura.png",
  "name_shaman.png",
];

/** The palette used by the converted novice meshes and by their honest fallback. */
const PREVIEW_PALETTE: CreaturePalette = { body: "#8a7a63", dark: "#554b3d" };

const PREVIEW_TURN_STEPS_PER_SECOND = HEADING_STEPS / 6;

function previewView(aspect: number): Mat4 {
  const view = lookAt([0, -9_000, 1_050], [0, 0, 800], [0, 0, 1]);
  const projection = perspective((24 * Math.PI) / 180, aspect, 100, 30_000);
  return multiply(projection, view);
}

/** `creation.Sex` / `ESex` (`common/length.h:145-149`). Wire values. */
export const SEX_MALE = 0;
export const SEX_FEMALE = 1;

/** `creation.NAME_MIN_LEN`, out of `locale_service.cpp:61-62`. */
export const NAME_MIN = 2;
/** `CHARACTER_NAME_MAX_LEN` (`common/length.h:13`). */
export const NAME_MAX = 24;

export function raceFor(offer: ClassOffer, sex: number): number | null {
  if (sex === SEX_MALE) return offer.male_race;
  if (sex === SEX_FEMALE) return offer.female_race;
  return null;
}

export function nameLooksSubmittable(name: string): boolean {
  const trimmed = name.trim();
  if (trimmed.length < NAME_MIN || trimmed.length > NAME_MAX) return false;
  return /^[0-9A-Za-z]+$/.test(trimmed);
}

export function firstEmptySlot(slots: readonly CharacterSlot[]): number {
  for (const slot of slots) if (!slot.filled) return slot.slot;
  return -1;
}

const JOB_DESC_URLS = [
  `${INTRO_ART}/jobdesc_warrior.txt`,
  `${INTRO_ART}/jobdesc_assassin.txt`,
  `${INTRO_ART}/jobdesc_sura.txt`,
  `${INTRO_ART}/jobdesc_shaman.txt`,
] as const;

const jobDesc: string[] = ["", "", "", ""];

/** The four stat bars of the creation board, each with the `ui.Gauge` colour
 *  its layout gives it: `hth_gauge` red, `int_gauge` pink, `str_gauge` purple,
 *  `dex_gauge` blue (`locale/en/ui/createcharacterwindow.py:180`, `:226`,
 *  `:272`, `:318`). The class is the chrome's, so the bar is the original's
 *  three-piece slot and its coloured fill rather than a flat colour. */
export const CREATE_STAT_ROWS: ReadonlyArray<readonly [string, "ht" | "iq" | "st" | "dx", string]> = [
  ["HP", "ht", ""],
  ["INT", "iq", "m2-gauge-pink"],
  ["STR", "st", "m2-gauge-purple"],
  ["DEX", "dx", "m2-gauge-blue"],
];

export function describe(job: number): string {
  if (job < 0 || job > 3) return "";
  return jobDesc[job];
}

export async function loadJobDescs(): Promise<void> {
  try {
    await Promise.all(
      JOB_DESC_URLS.map(async (url, job) => {
        const response = await fetch(url);
        if (!response.ok) return;
        jobDesc[job] = parseEventSet(await response.text());
      }),
    );
  } catch {
    return;
  }
}

const STYLE_ID = "charcreate-style";

const STYLE = `
/* The palette and the chrome are ui/intro.ts's, shared with the login screen
   and the select screen this opens over. Only what is peculiar to this screen
   is here.

   The layout is uiscript/createcharacterwindow.py's shape: the four classes
   offered as a row of boards (the original stands the four bodies up and names
   them under the same board frame), then the two things a player actually
   chooses -- body and name -- and one button. */
.charcreate {
  z-index: 70;
}

/* Wider than the shared anchor's default, for the row of four class cards.
   A fraction of the stage rather than of the window, so it stays inside the
   painting. */
.charcreate .intro-anchor {
  width: min(94%, 860px);
}

.charcreate-panel {
  width: 100%;
  padding: 14px 16px 16px;
}
.charcreate-head { margin-bottom: 14px; }
.charcreate-classes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
}
.charcreate-preview-layout {
  display: grid;
  grid-template-columns: 208px minmax(290px, 1fr);
  gap: 14px;
  align-items: stretch;
}
.charcreate-preview {
  position: relative;
  min-height: 340px;
  overflow: hidden;
  border: 1px solid var(--intro-edge-soft);
  background:
    radial-gradient(ellipse at 50% 78%, rgba(201, 164, 78, 0.19), transparent 51%),
    linear-gradient(rgba(5, 5, 4, 0.35), rgba(5, 5, 4, 0.78));
  box-shadow: inset 0 0 42px rgba(0, 0, 0, 0.7);
}
.charcreate-selected-plate {
  /* The legacy script overlays the selected 256x146 name sprite at
     BOARD_X - 27 / BOARD_Y - 149 (createcharacterwindow.py:28-64). The
     transparent PNG contains generous margins, so contain it instead of
     stretching its brush lettering into the preview frame. */
  position: absolute;
  top: 6px;
  left: 50%;
  z-index: 2;
  width: 180px;
  height: 78px;
  transform: translateX(-50%);
  background: center / contain no-repeat;
  filter: drop-shadow(0 0 1px rgba(255, 245, 225, 0.9)) drop-shadow(0 0 4px rgba(255, 235, 200, 0.45));
}
.charcreate-selected-stats {
  position: absolute;
  right: 12px;
  bottom: 28px;
  left: 12px;
  z-index: 2;
  padding: 7px 9px 6px;
  border: 1px solid rgba(169, 139, 69, 0.42);
  background: rgba(8, 7, 5, 0.72);
}
.charcreate-selected-stats[hidden] { display: none; }
.charcreate-selected-stats-title {
  margin: 0 0 4px;
  color: var(--intro-gold-dim);
  font-size: 0.63rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.charcreate-stat-gauge {
  display: grid;
  grid-template-columns: 30px 1fr 22px;
  gap: 5px;
  align-items: center;
  margin-top: 3px;
  color: var(--intro-ink-quiet);
  font-size: 0.62rem;
  font-variant-numeric: tabular-nums;
}
.charcreate-stat-gauge-track { width: 100%; }
.charcreate-stat-gauge-value { color: var(--intro-ink-bright); text-align: right; }
.charcreate-preview canvas {
  display: block;
  width: 100%;
  height: 340px;
}
.charcreate-preview-notice {
  position: absolute;
  top: 12px;
  right: 12px;
  left: 12px;
  z-index: 3;
  margin: 0;
  padding: 6px 8px;
  border: 1px solid rgba(169, 139, 69, 0.42);
  background: rgba(8, 7, 5, 0.82);
  color: var(--intro-ink-bright);
  font-size: 0.66rem;
  line-height: 1.35;
  text-align: center;
}
.charcreate-preview-notice[hidden] { display: none; }
.charcreate-preview-caption {
  position: absolute;
  right: 12px;
  bottom: 9px;
  left: 12px;
  margin: 0;
  color: var(--intro-ink-dim);
  font-size: 0.7rem;
  letter-spacing: 0.11em;
  text-align: center;
  text-transform: uppercase;
  pointer-events: none;
}
.charcreate-board {
  display: flex;
  flex-direction: column;
  min-height: 340px;
  padding: 8px 10px 10px;
}
.charcreate-board .charcreate-head { margin-bottom: 7px; }
.charcreate-board .charcreate-title { font-size: 1.05rem; }
.charcreate-board .charcreate-subtitle { font-size: 0.7rem; }
.charcreate-board .charcreate-classes {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.charcreate-board .charcreate-class {
  display: grid;
  grid-template-columns: 46px 1fr;
  column-gap: 6px;
  min-height: 0;
  padding: 4px 5px;
}
.charcreate-board .charcreate-class-plate {
  grid-row: span 2;
  width: 46px;
  height: 28px;
  margin: 0;
}
.charcreate-board .charcreate-class-blurb { display: none; }
.charcreate-board .charcreate-class-stats { gap: 2px 6px; font-size: 0.62rem; }
.charcreate-board .charcreate-row { margin-top: auto; display: block; }
.charcreate-board .charcreate-field { margin-top: 8px; }
.charcreate-board .charcreate-name {
  box-sizing: border-box;
  width: 90px;
  min-width: 90px;
  max-width: 100%;
}
.charcreate-board .charcreate-submit { width: 100%; margin: 12px 0 0; }
.charcreate-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-top: 12px;
}
.charcreate-actions .charcreate-submit,
.charcreate-actions .charcreate-cancel { width: 100%; margin: 0; }
.charcreate-class-list-label {
  margin: 13px 0 5px;
  color: var(--intro-ink-dim);
  font-size: 0.68rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.charcreate-class-nav {
  display: flex;
  gap: 5px;
  margin: 0 0 5px;
}
.charcreate-class-nav button {
  min-width: 30px;
  padding: 2px 8px;
  color: var(--intro-ink-bright);
}
@media (max-width: 660px) {
  .charcreate-preview-layout { grid-template-columns: 1fr; }
  .charcreate-preview, .charcreate-preview canvas { min-height: 270px; height: 270px; }
  .charcreate-board { min-height: 0; }
}
.charcreate-class {
  text-align: left;
  padding: 8px 10px;
  border: 1px solid var(--intro-edge-soft);
  background: rgba(0, 0, 0, 0.55);
  color: inherit;
  font: inherit;
  cursor: pointer;
}
.charcreate-class:hover { border-color: var(--intro-edge-strong); }
.charcreate-class[aria-pressed="true"] {
  border-color: var(--intro-button-top);
  background: rgba(90, 70, 26, 0.55);
  box-shadow: inset 0 0 0 1px rgba(169, 139, 69, 0.35);
}

/* The class name plate. Contained rather than stretched: the four sprites are
   256x146 with a lot of transparent margin, and squashing one to a card's
   aspect would tilt the brushwork. The strip is short because the plate is the
   card's heading, not its picture -- the original stands a whole character
   under it and this build has no character to stand there yet.

   The white outline is not decoration. The sprite is black brushwork with a
   thin red glow, authored to be read against the lit town street the original
   draws it over (createcharacterwindow.py:29-64 puts it high on select.jpg,
   where the sky is). On a dark card it disappears, which is what the first run
   of tools/e2e/intro.mjs photographed. Two drop-shadows put a light edge round
   the strokes without touching their colour, so the plate is still the
   original's picture and not a recoloured one. */
.charcreate-class-plate {
  height: 46px;
  margin: -2px 0 2px;
  background: center / contain no-repeat;
  filter:
    drop-shadow(0 0 1px rgba(255, 245, 225, 0.9))
    drop-shadow(0 0 4px rgba(255, 235, 200, 0.45));
}
.charcreate-class-name { font-weight: 600; color: var(--intro-gold); }
.charcreate-class-blurb { color: var(--intro-ink-dim); font-size: 0.76rem; margin: 3px 0 7px; }
.charcreate-class-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 3px 10px;
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
  color: var(--intro-ink-quiet);
}
.charcreate-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  margin-top: 18px;
  flex-wrap: wrap;
}
.charcreate-field { display: flex; flex-direction: column; gap: 5px; }
.charcreate-toggle { display: flex; gap: 6px; }
.charcreate-toggle button {
  padding: 6px 14px;
  border: 1px solid var(--intro-edge-soft);
  background: rgba(0, 0, 0, 0.55);
  color: var(--intro-gold-dim);
  font: inherit;
  cursor: pointer;
}
.charcreate-toggle button[aria-pressed="true"] {
  border-color: var(--intro-button-top);
  background: rgba(90, 70, 26, 0.6);
  color: var(--intro-gold);
}
.charcreate-name { min-width: 190px; }
.charcreate-submit { margin-left: auto; padding-left: 26px; padding-right: 26px; }
.charcreate-error { margin: 14px 0 0; min-height: 1.2em; }
.charcreate-error:empty { display: none; }
.charcreate-permanent {
  margin: 12px 0 0;
  color: var(--intro-ink-dim);
  font-size: 0.76rem;
}
`;

const installStyle = styleInstaller(STYLE_ID, STYLE, installChrome);

export class CharacterCreateScreen {
  private readonly root: HTMLElement;
  private readonly classList: HTMLElement;
  private readonly previewFrame: HTMLElement;
  private readonly selectedPlate: HTMLElement;
  private readonly selectedStats: HTMLElement;
  private readonly previewNotice: HTMLElement;
  private readonly preview: HTMLCanvasElement;
  private readonly previewContext: CanvasRenderingContext2D | null;
  private readonly meshes: CreatureMeshRenderer | null;
  private readonly sexButtons: HTMLButtonElement[] = [];
  private readonly shapeButtons: HTMLButtonElement[] = [];
  private readonly classPrevious: HTMLButtonElement;
  private readonly classNext: HTMLButtonElement;
  private readonly name: HTMLInputElement;
  private readonly submit: HTMLButtonElement;
  private readonly error: HTMLElement;

  /** The last frame the server sent. Every request is composed out of it. */
  private frame: Characters | null = null;

  private target: number | null = null;

  /** The chosen class, by job. Null until a frame has arrived to choose from. */
  private job: number | null = null;

  private sex = SEX_MALE;
  private shape = 0;

  /** True between sending a request and the server answering, so a second
   *  click cannot become a second character. The server would refuse the
   *  duplicate -- the slot is taken by then -- but a screen that let the button
   *  fire twice would show a refusal for a creation that worked. */
  private pending = false;
  private animationFrame: number | null = null;

  /** The previewed body's current turn, in `HEADING_STEPS`; see
   *  `PREVIEW_TURN_STEPS_PER_SECOND`. */
  private previewHeadingSteps = 0;
  /** `performance.now()`/rAF timestamp of the previous preview frame, so the
   *  turn advances by wall-clock time and not by however often a frame lands. */
  private previewLastFrameTime: number | null = null;

  constructor(
    container: HTMLElement,
    private readonly actions: CharacterCreateActions = {},
  ) {
    const doc = container.ownerDocument;
    // The shared chrome first: this screen's own rules read its custom
    // properties, and a stylesheet that arrived after them would repaint.
    installIntroStyle(doc);
    installStyle(doc);

    // The same letterboxed stage and the same backdrop as the select screen
    // this opens over: `createcharacterwindow.py:26` draws `select.sub` too,
    // so the two screens really are one scene in the original.
    const { ground, anchor } = introGround(doc, "select");
    this.root = ground;
    this.root.classList.add("charcreate");
    this.root.hidden = true;
    anchor.innerHTML = TEMPLATE;
    container.append(this.root);

    this.classList = this.must(".charcreate-classes");
    this.previewFrame = this.must(".charcreate-preview");
    this.selectedPlate = this.must(".charcreate-selected-plate");
    this.selectedStats = this.must(".charcreate-selected-stats");
    this.previewNotice = this.must(".charcreate-preview-notice");
    this.preview = this.must<HTMLCanvasElement>(".charcreate-preview-canvas");
    let previewContext: CanvasRenderingContext2D | null = null;
    try {
      previewContext = this.preview.getContext("2d", { alpha: true });
    } catch {
      // DOM-only environments may expose a canvas without a 2D context.
    }
    this.previewContext = previewContext;

    // `CreatureRenderer.OnRender` owns a dedicated viewport in the original
    // (`root/introcreate.py:78-94`). Reuse the browser's converted body meshes
    // in a transparent layer, with Canvas2D's humanoid as the honest fallback
    // when WebGL2 or a body asset is unavailable.
    let meshes: CreatureMeshRenderer | null = null;
    try {
      meshes = new CreatureMeshRenderer(this.preview);
    } catch {
      // Canvas2D remains the visual fallback when WebGL2 is unavailable.
    }
    this.meshes = meshes;
    void loadJobDescs().then(() => {
      if (this.frame !== null) this.paint();
    });
    this.name = this.must<HTMLInputElement>(".charcreate-name");
    this.submit = this.must<HTMLButtonElement>(".charcreate-submit");
    this.error = this.must(".charcreate-error");
    this.classPrevious = this.must<HTMLButtonElement>(".charcreate-class-prev");
    this.classNext = this.must<HTMLButtonElement>(".charcreate-class-next");
    this.classPrevious.addEventListener("click", () => this.cycleClass(-1));
    this.classNext.addEventListener("click", () => this.cycleClass(1));
    this.must<HTMLButtonElement>(".charcreate-cancel").addEventListener("click", () => {
      if (!this.pending) this.actions.onCancel?.();
    });

    // The legacy creation window lets the number keys 2..5 select its four
    // character slots (`introcreate.py:531-546`). Keep those shortcuts tied to
    // the server-offered class order, so a shard offering fewer classes cannot
    // select an invented one.
    this.root.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        this.cycleClass(event.key === "ArrowLeft" ? -1 : 1);
        return;
      }
      const index = ["2", "3", "4", "5"].indexOf(event.key);
      if (index >= 0) {
        if (this.frame?.classes[index] === undefined) return;
        event.preventDefault();
        this.job = this.frame.classes[index].job;
        this.paint();
        return;
      }
      // Python's key codes 59/60 are the first two function keys in the
      // legacy input table; OnKeyDown maps them to MAN_PAGE/WOMAN_PAGE
      // (`introcreate.py:547-550`).
      const sex = event.key === "F1" ? SEX_MALE : event.key === "F2" ? SEX_FEMALE : null;
      if (sex === null) return;
      event.preventDefault();
      this.sex = sex;
      this.paint();
    });

    for (const button of Array.from(
      this.root.querySelectorAll<HTMLButtonElement>(".charcreate-sex button"),
    )) {
      this.sexButtons.push(button);
      button.addEventListener("click", () => {
        this.sex = Number(button.dataset.sex);
        this.paint();
      });
    }
    for (const button of Array.from(
      this.root.querySelectorAll<HTMLButtonElement>(".charcreate-shape button"),
    )) {
      this.shapeButtons.push(button);
      button.addEventListener("click", () => {
        this.shape = Number(button.dataset.shape);
        this.paint();
      });
    }

    this.name.addEventListener("input", () => {
      // A server refusal describes the previous name/request. Once the player
      // edits the name, keeping that message visible makes the corrected form
      // look refused before it has been sent.
      this.error.textContent = "";
      this.refreshSubmit();
    });
    this.name.addEventListener("keydown", (event) => {
      event.stopPropagation();
      // `introcreate.py:218` binds Escape on the name edit line to
      // CancelCreate, so keyboard dismissal must be equivalent to the visible
      // Cancel button and must not become a creation request.
      if (event.key === "Escape") {
        event.preventDefault();
        if (!this.pending) this.actions.onCancel?.();
        return;
      }
      if (event.key !== "Enter") return;
      event.preventDefault();
      this.send();
    });

    for (const event of ["keyup", "keypress"]) {
      this.name.addEventListener(event, (raw) => raw.stopPropagation());
    }
    this.must<HTMLFormElement>("form").addEventListener("submit", (event) => {
      event.preventDefault();
      this.send();
    });
  }

  private must<T extends Element>(selector: string): T {
    const found = this.root.querySelector<T>(selector);
    if (found === null) throw new Error(`charcreate template is missing ${selector}`);
    return found as T;
  }

  update(frame: Characters, slot: number | null = null): void {
    this.frame = frame;
    this.pending = false;
    // A slot that is no longer empty is dropped rather than kept, so a screen
    // reopened after somebody else filled that box falls back to a free one
    // instead of composing a request that can only be refused.
    const wanted = frame.slots.find((one) => one.slot === slot);
    this.target = wanted !== undefined && !wanted.filled ? slot : null;

    if (firstEmptySlot(frame.slots) < 0) {
      this.dismiss();
      return;
    }

    // The default class is the first the server offered, never a constant here.
    // A shard that offered three would otherwise open on a class it does not
    // have.
    if (this.job === null && frame.classes.length > 0) {
      this.job = frame.classes[0].job;
    }

    this.root.hidden = false;
    this.paint();
    this.name.focus();
  }

  fail(text: string): void {
    this.release();
    this.error.textContent = text;
  }

  /** Unlock the form after a refusal the 2014 client showed nothing for. */
  release(): void {
    this.pending = false;
    this.refreshSubmit();
  }

  /** Take the screen down. The world is next. */
  dismiss(): void {
    this.root.hidden = true;
    if (this.animationFrame !== null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  /** True while the screen is up, which is what the boot sequence waits on. */
  get open(): boolean {
    return !this.root.hidden;
  }

  // ------------------------------------------------------------------ private

  private paint(): void {
    const frame = this.frame;
    if (frame === null) return;

    this.classList.replaceChildren();
    const doc = this.root.ownerDocument;
    for (const offer of frame.classes) {
      const card = doc.createElement("button");
      card.type = "button";
      card.className = "charcreate-class";
      card.dataset.job = String(offer.job);
      card.setAttribute("aria-pressed", String(offer.job === this.job));

      const plate = elem(doc, "div", "charcreate-class-plate");
      const sprite = JOB_PLATE[offer.job];
      if (sprite !== undefined) {
        plate.style.backgroundImage = `url("${INTRO_ART}/${sprite}")`;
      }
      plate.setAttribute("role", "img");
      plate.setAttribute("aria-label", offer.name);

      const name = elem(doc, "div", "charcreate-class-name", offer.name);

      const blurb = elem(doc, "p", "charcreate-class-blurb", describe(offer.job));

      const stats = elem(doc, "div", "charcreate-class-stats");
      // The server's numbers, spelled out. Nothing here is computed: `max_hp`
      // and `max_sp` arrived already through `char.cpp:2172-2173`.
      for (const [label, value] of [
        ["STR", offer.st],
        ["CON", offer.ht],
        ["DEX", offer.dx],
        ["INT", offer.iq],
        ["HP", offer.max_hp],
        ["SP", offer.max_sp],
      ] as ReadonlyArray<readonly [string, number]>) {
        const span = doc.createElement("span");
        span.textContent = `${label} ${value}`;
        stats.append(span);
      }

      card.append(plate, name, blurb, stats);
      card.addEventListener("click", () => {
        this.job = offer.job;
        this.paint();
      });
      this.classList.append(card);
    }

    for (const button of this.sexButtons) {
      button.setAttribute("aria-pressed", String(Number(button.dataset.sex) === this.sex));
    }
    for (const button of this.shapeButtons) {
      button.setAttribute("aria-pressed", String(Number(button.dataset.shape) === this.shape));
    }

    this.paintPreview();
    this.refreshSubmit();
  }

  private cycleClass(delta: -1 | 1): void {
    const classes = this.frame?.classes ?? [];
    if (classes.length === 0) return;
    const current = Math.max(0, classes.findIndex((entry) => entry.job === this.job));
    const next = (current + delta + classes.length) % classes.length;
    this.job = classes[next].job;
    this.paint();
  }

  private paintPreview(): void {
    const frame = this.frame;
    const offer = frame?.classes.find((entry) => entry.job === this.job);
    const race = offer === undefined ? null : raceFor(offer, this.sex);
    this.previewFrame.dataset.job = this.job === null ? "" : String(this.job);
    this.previewFrame.dataset.race = race === null ? "" : String(race);
    // Exposed for the same reason `dataset.job`/`dataset.race` are: a test (or
    // a person with devtools open) can see the turntable substitute is actually
    // advancing without needing a WebGL context to read it back out of a frame.
    this.previewFrame.dataset.heading = String(Math.round(this.previewHeadingSteps));
    this.previewFrame.setAttribute(
      "aria-label",
      offer === undefined ? "Character preview" : `${offer.name} character preview`,
    );
    const sprite = offer === undefined ? undefined : JOB_PLATE[offer.job];
    if (sprite === undefined) {
      this.selectedPlate.style.backgroundImage = "none";
    } else {
      this.selectedPlate.style.backgroundImage = `url("${INTRO_ART}/${sprite}")`;
    }
    this.selectedPlate.setAttribute("aria-label", offer?.name ?? "Character preview");
    this.paintSelectedStats(offer);

    const cssWidth = this.preview.clientWidth || 400;
    const cssHeight = this.preview.clientHeight || 340;
    const dpr = globalThis.devicePixelRatio || 1;
    const ctx = this.previewContext;
    if (ctx !== null) {
      this.preview.width = Math.round(cssWidth * dpr);
      this.preview.height = Math.round(cssHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssWidth, cssHeight);
    }

    const mesh = this.meshes;
    const key = race === null ? null : mesh?.baseBodyKey(race) ?? null;
    const status = this.previewStatus(mesh, key, race);
    const ready = key !== null && status?.kind === "ready";
    this.paintPreviewNotice(status);
    this.previewFrame.dataset.preview = ready ? "mesh" : "silhouette";
    const viewProj = previewView(cssWidth / Math.max(1, cssHeight));
    if (mesh !== null && ready) {
      mesh.beginFrame(Math.round(cssWidth * dpr), Math.round(cssHeight * dpr));
      // The standalone renderer inserts its transparent WebGL canvas lazily at
      // the first frame (its constructor cannot know the host's final parent).
      // Pin that layer to this preview, rather than leaving the stack helper's
      // full-viewport canvas over the entire intro screen.
      const meshCanvas = this.preview.previousElementSibling;
      if (meshCanvas instanceof HTMLElement) {
        meshCanvas.classList.add("charcreate-preview-mesh");
        meshCanvas.style.position = "absolute";
        meshCanvas.style.left = "0";
        meshCanvas.style.top = "0";
        meshCanvas.style.width = "100%";
        meshCanvas.style.height = "100%";
        meshCanvas.style.zIndex = "0";
        meshCanvas.style.pointerEvents = "none";
      }
      const entityId = 0x6000 + (race ?? 0);
      mesh.draw(
        key,
        entityId,
        0,
        0,
        0,
        this.previewHeadingSteps,
        Gait.IDLE,
        viewProj,
        PREVIEW_PALETTE,
      );
      const hairKey = race === null ? null : mesh.defaultHairKey(race);
      if (hairKey !== null && mesh.status(hairKey).kind === "ready") {
        mesh.draw(
          hairKey,
          hairEntityId(entityId),
          0,
          0,
          0,
          this.previewHeadingSteps,
          Gait.IDLE,
          viewProj,
          PREVIEW_PALETTE,
          null,
          key,
          entityId,
        );
      }
    }

    // A converted body can be loading for the first few frames, and a shard
    // can ship a race with no body at all. Keep a recognizable person on the
    // glass until the real model is ready, exactly as the world renderer does.
    if (ctx !== null && !ready) {
      const fallback: CreatureFrame = {
        x: cssWidth * 0.5,
        y: cssHeight * 0.56,
        radius: Math.max(52, Math.min(92, cssHeight * 0.23)),
        angle: (this.previewHeadingSteps / HEADING_STEPS) * Math.PI * 2,
        distanceMm: 0,
        resting: true,
        palette: PREVIEW_PALETTE,
      };
      drawCreature(ctx, "humanoid", fallback);
    }
    this.startPreviewAnimation();
  }

  private previewStatus(
    mesh: CreatureMeshRenderer | null,
    key: string | null,
    race: number | null,
  ): MeshStatus | null {
    if (race === null) return null;
    if (mesh === null) {
      return { kind: "unavailable", reason: "this browser gave the preview no WebGL2 layer" };
    }
    return mesh.status(key ?? `race ${race}`);
  }

  private paintPreviewNotice(status: MeshStatus | null): void {
    const reason =
      status === null || status.kind === "ready" || status.kind === "loading"
        ? null
        : status.reason;
    this.previewFrame.dataset.previewReason = reason ?? "";
    if (reason === null) {
      this.previewNotice.textContent = "";
      this.previewNotice.hidden = true;
      return;
    }
    this.previewNotice.textContent =
      `The character model could not be drawn here, so the outline below stands in for it — ${reason}.`;
    this.previewNotice.hidden = false;
  }

  private paintSelectedStats(offer: ClassOffer | undefined): void {
    this.selectedStats.replaceChildren();
    if (offer === undefined) {
      this.selectedStats.hidden = true;
      return;
    }
    this.selectedStats.hidden = false;
    this.selectedStats.dataset.job = String(offer.job);
    const title = elem(
      this.selectedStats.ownerDocument,
      "p",
      "charcreate-selected-stats-title",
      `${offer.name} · starting attributes`,
    );
    this.selectedStats.append(title);
    const total = offer.st + offer.ht + offer.dx + offer.iq;
    for (const [label, field, colour] of CREATE_STAT_ROWS) {
      const value = offer[field];
      const row = elem(this.selectedStats.ownerDocument, "div", "charcreate-stat-gauge");
      row.dataset.stat = label;
      const name = this.selectedStats.ownerDocument.createElement("span");
      name.textContent = label;
      const track = elem(
        this.selectedStats.ownerDocument,
        "span",
        `charcreate-stat-gauge-track m2-gauge ${colour}`.trimEnd(),
      );
      const fill = elem(this.selectedStats.ownerDocument, "span", "m2-gauge-fill");
      fill.style.setProperty(
        "--m2-gauge-percent",
        `${total > 0 ? (value / total) * 100 : 0}%`,
      );
      track.append(fill);
      const number = elem(
        this.selectedStats.ownerDocument,
        "span",
        "charcreate-stat-gauge-value",
        String(value),
      );
      row.append(name, track, number);
      this.selectedStats.append(row);
    }
  }

  private startPreviewAnimation(): void {
    if (this.animationFrame !== null || typeof requestAnimationFrame !== "function") return;
    this.animationFrame = requestAnimationFrame((now) => {
      this.animationFrame = null;
      if (this.root.hidden) {
        // Off screen; forget the clock so reopening does not read a huge
        // elapsed gap as one giant turn.
        this.previewLastFrameTime = null;
        return;
      }
      const last = this.previewLastFrameTime ?? now;
      // Clamped so a stalled tab (or the very first frame) advances the turn
      // by at most a quarter turn rather than snapping the body around.
      const elapsedSeconds = Math.min(0.25, Math.max(0, (now - last) / 1000));
      this.previewLastFrameTime = now;
      this.previewHeadingSteps =
        (this.previewHeadingSteps + elapsedSeconds * PREVIEW_TURN_STEPS_PER_SECOND) % HEADING_STEPS;
      this.paintPreview();
    });
  }

  request(): {
    slot: number;
    name: string;
    race: number;
    shape: number;
  } | null {
    const frame = this.frame;
    if (frame === null || this.job === null) return null;
    if (!frame.may_create) return null;

    const offer = frame.classes.find((entry) => entry.job === this.job);
    if (offer === undefined) return null;

    const race = raceFor(offer, this.sex);
    if (race === null) return null;

    const slot = this.target ?? firstEmptySlot(frame.slots);
    if (slot < 0) return null;

    const name = this.name.value.trim();
    if (!nameLooksSubmittable(name)) return null;

    // `shape` is the starting outfit, with the two legal values exposed by the
    // legacy shape_button_01/02 pair (`createcharacterwindow.py:474-509`).
    return { slot, name, race, shape: this.shape };
  }

  private refreshSubmit(): void {
    this.submit.disabled = this.pending || this.request() === null;
  }

  private send(): void {
    const request = this.request();
    if (request === null || this.pending) return;
    this.pending = true;
    this.error.textContent = "";
    this.submit.disabled = true;
    this.actions.onCreate?.(
      request.slot,
      request.name,
      request.race,
      request.shape,
    );
  }
}

const TEMPLATE = `
  <div class="intro-board intro-board-dense charcreate-panel">
    <header class="charcreate-head">
      <h1 class="intro-title charcreate-title">Make a character</h1>
      <p class="intro-subtitle charcreate-subtitle">Four classes, two bodies each. The class is chosen once.</p>
    </header>

    <form autocomplete="off">
      <div class="charcreate-preview-layout">
        <section class="intro-board charcreate-board" aria-label="Character details">
          <span class="charcreate-class-list-label">Class</span>
          <div class="charcreate-class-nav" role="group" aria-label="Change class">
            <button type="button" class="intro-button charcreate-class-prev" aria-label="Previous class">‹</button>
            <button type="button" class="intro-button charcreate-class-next" aria-label="Next class">›</button>
          </div>
          <div class="charcreate-classes"></div>

          <div class="charcreate-row">
        <!-- A div and not a label. Buttons are labelable elements, so a
             <label> wrapping these two would forward every click inside it to
             the *first* of them: choosing Female would fire Female and then
             Male, and the screen would silently stay on the male body. -->
            <!-- The source names this field CREATE_SEX (createcharacterwindow.py:431-438).
                 Its label and the two buttons are the shipped words, not translated
                 ones: locale_interface.txt:28 CREATE_SEX "Gender", :24 CREATE_MAN
                 "Man", :32 CREATE_WOMAN "Woman" -- confirmed by the F1/F2 shortcut
                 comment above, whose own OnKeyDown maps to MAN_PAGE/WOMAN_PAGE
                 (introcreate.py:547-550). -->
            <div class="charcreate-field charcreate-sex" role="group" aria-label="Gender">
              <span class="intro-label">Gender</span>
              <span class="charcreate-toggle">
                <button type="button" data-sex="0" aria-pressed="true">Man</button>
                <button type="button" data-sex="1" aria-pressed="false">Woman</button>
              </span>
            </div>
            <div class="charcreate-field charcreate-shape" role="group" aria-label="Shape">
              <span class="intro-label">Shape</span>
              <span class="charcreate-toggle">
                <button type="button" data-shape="0" aria-pressed="true">1</button>
                <button type="button" data-shape="1" aria-pressed="false">2</button>
              </span>
            </div>

            <label class="charcreate-field">
              <span class="intro-label">Name</span>
              <!-- Legacy editline input_limit is 12 (createcharacterwindow.py:420-427). -->
              <input class="intro-field charcreate-name" type="text" maxlength="12"
                autocapitalize="none" autocorrect="off" spellcheck="false">
            </label>

            <div class="charcreate-actions">
              <button type="submit" class="intro-button intro-button-primary charcreate-submit" disabled>Create</button>
              <button type="button" class="intro-button intro-button-quiet charcreate-cancel">Cancel</button>
            </div>
          </div>
        </section>

        <div class="charcreate-preview" role="img" aria-label="Character preview">
          <!-- createcharacterwindow.py:28-64 places one class plate above the
               character board. Keep a single prominent selected plate here;
               the four card plates remain below as keyboard-accessible choices. -->
          <div class="charcreate-selected-plate" role="img" aria-label="Character preview"></div>
          <div class="charcreate-selected-stats" aria-label="Starting attributes" hidden></div>
          <canvas class="charcreate-preview-canvas" aria-hidden="true"></canvas>
          <!-- Said out loud rather than left to a shrug: a browser that gives
               this screen no WebGL2 layer draws the Canvas2D outline instead,
               and a player who is looking at an outline is entitled to know
               that is what it is. See paintPreview below. -->
          <p class="charcreate-preview-notice" role="status" hidden></p>
          <p class="charcreate-preview-caption">Idle preview · choose a class</p>
        </div>
      </div>

      <p class="intro-error charcreate-error"></p>
      <p class="charcreate-permanent">Letters and digits only. Nothing here can be changed later.</p>
    </form>
  </div>
`;
